import { Injectable, OnDestroy } from '@angular/core';

import moment from 'moment-timezone';
import { MessageService } from 'primeng/api';
import { catchError, Observable, of, OperatorFunction, share, Subject, Subscription } from 'rxjs';
import { switchMap, take, tap, filter } from 'rxjs/operators';

import { TVEyesArchivedStateEnum } from '../../../api/mentions/enum/tv-eyes-archived-state.enum';
import { MentionsApi, TVEyesArchivePatchModel } from '../../../api/mentions/mentions.api';
import { MediaViewRequestApiModel } from '../../../api/mentions/model/instaplay-request.api.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { MentionsService } from '../../../Monitor/Core/services/mentions.service';
import { IframeDialogComponent } from '../../Dialogs/components/IframeDialog/iframe-dialog.component';
import { TVEyesArchiveDialogComponent } from '../../Dialogs/components/TVEyesArchiveDialog/tveyes-archive-dialog.component';
import { TVEyesMediaViewDialogComponent } from '../../Dialogs/components/TVEyesMediaViewDialog/tveyes-mediaview-dialog.component';
import { TVEyesMediaViewIframeDialogModel } from '../../Dialogs/components/TVEyesMediaViewDialog/tveyes-mediaview-iframe-dialog.model';
import { IframeDialogModel } from '../../Dialogs/models/iframe-dialog.model';
import { TVEyesArchiveDialogModel } from '../../Filter/models/tveyes-archive-dialog.model';
import { messageSeverityEnum } from '../../Misc/enums/message-severity.enum';
import { KeywordHighlightModel } from '../../Misc/models/keyword-highlight.model';
import { Mention } from '../models/mention';
import { MentionSearchModel } from '../../../Monitor/Shared/models/mention-search.model';

interface TVEyesEditorMessage {
    action: string;
    properties: {
        startDateTime: string;
        endDateTime: string;
        type: 'archive' | 'instantplay' | 'download';
        transcript: {
            text: string;
            timestamp: number;
            dateTime: string;
        }[];
        instantPlayURL?: string;
    };
}

@Injectable()
export class TVEyesMentionsService implements OnDestroy {
    private subscriptions: Subscription[] = [];
    private updateMentionSubject = new Subject<TVEyesArchivePatchModel>();

    constructor(
        private mentionsApi: MentionsApi,
        private mentionsService: MentionsService,
        private currentUserStateService: CurrentUserStateService,
        private coreDialogService: CoreDialogService,
        private messageService: MessageService
    ) {
        const updateMention$ = this.updateMentionSubject.pipe(
            switchMap(patchModel => this.updateTVEyesArchiveStatus(patchModel)),
            tap(() => this.coreDialogService.closeViaComponent(TVEyesMediaViewDialogComponent.TVEYES_MEDIAVIEW_DIALOG_KEY)),
            share()
        );

        this.subscriptions.push(
            updateMention$.subscribe()
        );
    }

    openTVEyesDialog(mention: Mention, mentionSearch?: MentionSearchModel, keywords?: string[], enablePrecisionClip: boolean = true): Observable<void> {
        const onError: OperatorFunction<void, void> = catchError((err: Error) => {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: 'Mention.details.openMediaEditor.error.summary',
                detail: 'Mention.details.openMediaEditor.error.details'
            });
            return of(null);
        });

        if (mention.tvEyesArchiveState === TVEyesArchivedStateEnum.COMPLETED) {
            return this.openTVEyesArchiveDialog(mention).pipe(
                onError
            );
        }

        // After an edit, the clip will be processing or edited which can be viewed in the tveyes iframe
        // we want to show the proper edited start time and not rely on playstartregex
        const isModified = [TVEyesArchivedStateEnum.PROCESSING, TVEyesArchivedStateEnum.EDITED].includes(mention.tvEyesArchiveState);

        return this.getMediaViewUrl(mention, keywords, !isModified).pipe(
            switchMap(url => this.openTVEyesMediaViewDialog(mention, url, mentionSearch, keywords, enablePrecisionClip)),
            onError,
        );
    }

    openTvEyesPodcastPlayer(podcastId: string, highlightKeywords: KeywordHighlightModel[]): void {
        const url = new URL(`https://online.tveyes.com/onlineplayer/podcast/${podcastId}?partnerid=16087`);

        if (highlightKeywords) {
            const keywords: string[] = highlightKeywords
                ?.flatMap(x => x.keywords) ?? [];

            url.searchParams.append('query', keywords.join(','));
            url.searchParams.append('highlightindex', '0');
        }
        const iframeConfig: IframeDialogModel = {
            inputs: {
                id: 'tvEyesPodcastPlayer',
                heading: 'Mention.details.tvEyesPlayer',
                url: url.toString(),
                minHeight: '800px',
                minWidth: '900px',
                maxWidth: '1100px'
            }
        };
        this.coreDialogService.open(IframeDialogComponent, iframeConfig);
    }


    updateTVEyesArchiveStatus(patchModel: TVEyesArchivePatchModel): Observable<null> {
        return this.mentionsService.patchTVEyesMention(patchModel);
    }

    getMediaViewUrlFromPlayerlette(mention: Mention, playerletteUrl: string, keywords?: string[], usePlayStartRegex?: boolean): Observable<string> {
        return this.currentUserStateService.getAccount().pipe(
            take(1),
            filter(x => !!x),
            switchMap(account => {
                const requestBody: MediaViewRequestApiModel = {
                    url: playerletteUrl,
                    accountId: account.id,
                    mentionId: mention.mentionId,
                    keywords: keywords ?? [],
                };
                return this.mentionsApi.getTVEyesMediaViewUrl(requestBody, usePlayStartRegex);
            })
        );
    }

    private getMediaViewUrl(mention: Mention, keywords?: string[], usePlayStartRegex?: boolean): Observable<string> {
        return this.currentUserStateService.getAccount().pipe(
            take(1),
            filter(x => !!x),
            switchMap(account => {
                const requestBody: MediaViewRequestApiModel = {
                    url: mention.url,
                    accountId: account.id,
                    mentionId: mention.mentionId,
                    keywords: keywords ?? [],
                };
                return this.mentionsApi.getTVEyesMediaViewUrl(requestBody, usePlayStartRegex);
            })
        );
    }

    openTVEyesMediaViewDialog(
        mention: Mention,
        mediaViewUrl: string,
        mentionSearch?: MentionSearchModel,
        keywords?: string[],
        enablePrecisionCliping: boolean = true,
    ): Observable<any> {
        const url = new URL(mediaViewUrl);
        const iframeConfig: IframeDialogModel = {
            inputs: {
                id: 'tvEyesEditor',
                heading: 'Mention.details.tvEyesEditor',
                url: url.toString(),
                minHeight: '800px',
                minWidth: '900px',
                allowAutoplay: true,
            },
            outputs: {
                message: (message) => {
                    this.handleTVEyesEditorMessages(mention, message);
                },
            }
        };
        const mediaViewDialogConfig: TVEyesMediaViewIframeDialogModel = {
            inputs: {
                mention: mention,
                mentionSearch: mentionSearch,
                enablePrecisionClip: enablePrecisionCliping,
                mediaviewIFrameSettings: iframeConfig,
                keywords: keywords
            },
            outputs: {
                message: (message) => {
                    this.handleTVEyesEditorMessages(mention, message);
                },
            }
        };
        return this.coreDialogService.openWithObservable(TVEyesMediaViewDialogComponent, mediaViewDialogConfig);
    }

    private openTVEyesArchiveDialog(mention: Mention): Observable<void> {
        const url = new URL(mention.url);
        url.protocol = 'https';
        const tvEyesArchiveConfig: TVEyesArchiveDialogModel = {
            inputs: {
                iFrameURL: url.toString(),
                mention: mention,
            },
            outputs: {}
        };
        return this.coreDialogService.openWithObservable(TVEyesArchiveDialogComponent, tvEyesArchiveConfig);
    }

    /**
     * Used to parse and handle submission requests within the TVEyes MediaView Editor.
     * Emits into the {@link updateMentionSubject}
     */
    private handleTVEyesEditorMessages(mention: Mention, jsonMessage: string): void {
        const message: TVEyesEditorMessage = JSON.parse(jsonMessage);

        if (message?.action !== 'submitClipRequestComplete') {
            return;
        }

        const messageProps: TVEyesEditorMessage['properties'] = message.properties;
        const fullTranscript = messageProps.transcript.map(part => part.text).join(' ');
        const start = moment(messageProps.startDateTime, 'MM/DD/YYYY HH:mm:ss.SSS');
        const end = moment(messageProps.endDateTime, 'MM/DD/YYYY HH:mm:ss.SSS');
        const duration = Math.abs(end.diff(start, 'seconds'));

        let archivedState: TVEyesArchivedStateEnum;
        let instaplayUrl: string;
        switch(message.properties.type) {
            case 'archive':
                archivedState = TVEyesArchivedStateEnum.PROCESSING;
                break;
            case 'instantplay':
                archivedState = TVEyesArchivedStateEnum.EDITED;
                instaplayUrl = messageProps.instantPlayURL;
                break;
            case 'download':
                // No further action needed
                return;
            default:
                throw new Error(`No action for tvEyes Message of type ${message.properties.type}`);
        }

        const utcDate = moment.utc(messageProps.startDateTime, 'MM/DD/YYYY HH:mm:ss.SSS').valueOf();

        this.updateMentionSubject.next({
            id: mention.mentionId,
            isDirectArchive: false,
            body: fullTranscript,
            clipDuration: duration,
            tveyesArchivedState: archivedState,
            tvEyesInstaPlayUrl: instaplayUrl,
            startDateTime: utcDate
        });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
